
import { useState, FormEvent, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/use-user';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

type SocialMediaPlatform = "instagram" | "twitter" | "facebook" | "youtube" | "website";

interface SocialMediaLink {
  platform: SocialMediaPlatform;
  url: string;
}

interface ProfileData {
  username?: string;
  bio?: string;
  location?: string;
  website?: string;
  social_links?: SocialMediaLink[];
}

interface EditProfileProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onProfileUpdate: () => void;
}

export function EditProfile({ isOpen, onOpenChange, onProfileUpdate }: EditProfileProps) {
  const { user, refreshUser } = useUser();
  const [formData, setFormData] = useState<ProfileData>({
    username: '',
    bio: '',
    location: '',
    website: '',
    social_links: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Platform options for social media links
  const platformOptions: SocialMediaPlatform[] = ["instagram", "twitter", "website"];

  useEffect(() => {
    if (user) {
      let socialLinks: SocialMediaLink[] = [];
      
      // Parse social_links from user data if it exists
      if (user.social_links) {
        try {
          // If it's a string, parse it, otherwise use it directly
          const rawLinks = typeof user.social_links === 'string' 
            ? JSON.parse(user.social_links) 
            : (user.social_links as any);
          
          // Ensure it's an array and convert to correct type
          if (Array.isArray(rawLinks)) {
            socialLinks = rawLinks.map(link => ({
              platform: link.platform as SocialMediaPlatform,
              url: link.url
            }));
          }
        } catch (e) {
          console.error("Error parsing social_links:", e);
          socialLinks = [];
        }
      }
      
      setFormData({
        username: user.username || '',
        bio: user.bio || '',
        location: user.location || '',
        website: user.website || '',
        social_links: socialLinks,
      });
    }
  }, [user]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    setIsSaving(true);
    
    try {
      // Prepare the update data
      const updateData: Record<string, any> = {
        username: formData.username,
        bio: formData.bio,
        location: formData.location,
        website: formData.website,
      };
      
      // Only include social_links if they exist
      if (formData.social_links && formData.social_links.length > 0) {
        updateData.social_links = formData.social_links;
      }
      
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast.success('Perfil atualizado com sucesso!');
      
      // Refresh user data and close modal
      refreshUser();
      onProfileUpdate();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(`Erro ao atualizar perfil: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSocialLinkChange = (index: number, field: 'platform' | 'url', value: string) => {
    setFormData(prev => {
      const newLinks = [...(prev.social_links || [])];
      if (field === 'platform') {
        newLinks[index] = { 
          ...newLinks[index], 
          platform: value as SocialMediaPlatform 
        };
      } else {
        newLinks[index] = {
          ...newLinks[index],
          url: value
        };
      }
      return { ...prev, social_links: newLinks };
    });
  };

  const addSocialLink = () => {
    setFormData(prev => {
      const newLinks = [...(prev.social_links || []), { platform: 'instagram' as SocialMediaPlatform, url: '' }];
      return { ...prev, social_links: newLinks };
    });
  };

  const removeSocialLink = (index: number) => {
    setFormData(prev => {
      const newLinks = [...(prev.social_links || [])];
      newLinks.splice(index, 1);
      return { ...prev, social_links: newLinks };
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Perfil</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSave} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="username" className="text-sm font-medium">Nome de usuário</label>
            <Input
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Seu nome de usuário"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="bio" className="text-sm font-medium">Bio</label>
            <Textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              placeholder="Conte um pouco sobre você"
              className="resize-none h-20"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="location" className="text-sm font-medium">Localização</label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="Sua cidade/país"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="website" className="text-sm font-medium">Website</label>
            <Input
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://seusite.com"
            />
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium">Redes Sociais</label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addSocialLink}
                disabled={formData.social_links && formData.social_links.length >= 3}
              >
                Adicionar
              </Button>
            </div>
            
            {formData.social_links && formData.social_links.map((link, index) => (
              <div key={index} className="flex gap-2 items-center">
                <select
                  value={link.platform}
                  onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                  className="flex h-10 w-1/3 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {platformOptions.map((platform) => (
                    <option key={platform} value={platform}>
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                    </option>
                  ))}
                </select>
                
                <Input
                  value={link.url}
                  onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                  placeholder="URL da rede social"
                  className="w-full"
                />
                
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon"
                  onClick={() => removeSocialLink(index)}
                  className="h-10 w-10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
